import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCrosshairsData } from "@/providers";
import {
  getGeneratedPageDataConfigError,
  resolveGeneratedPageDataSource,
} from "./dataSource";
import { resolveGeneratedPage } from "./registry";

interface IGeneratedPageHostProps {
  data?: any;
}

interface IMockState {
  data: Record<string, any> | null;
  isLoading: boolean;
  error: Error | null;
}

const renderState = (title: string, description: string) => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-[24px] text-center">
      <p className="text-[18px] font-[700] leading-[25px] text-[#fafafa]">{title}</p>
      <p className="mt-[8px] text-[14px] leading-[20px] text-[#a1a1aa]">{description}</p>
    </div>
  );
};

const GeneratedPageHost: React.FC<IGeneratedPageHostProps> = ({ data }) => {
  const location = useLocation();
  const page = resolveGeneratedPage(location.pathname);
  const pageDataConfig = page?.data;
  const pageId = page?.pageId ?? location.pathname;
  const mockSearchParam = new URLSearchParams(location.search).get("mock");
  const source = resolveGeneratedPageDataSource(pageDataConfig, mockSearchParam);
  const configError = data === undefined ? getGeneratedPageDataConfigError(source, pageDataConfig) : null;
  const remoteKeys = data === undefined && source === "remote" ? pageDataConfig?.dataKeys ?? [] : [];
  const remoteResult = useCrosshairsData(remoteKeys);
  const [mockState, setMockState] = useState<IMockState>({
    data: null,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (data !== undefined || source !== "mock" || configError) {
      setMockState({
        data: null,
        isLoading: false,
        error: null,
      });
      return;
    }

    if (pageDataConfig?.mockData) {
      setMockState({
        data: pageDataConfig.mockData,
        isLoading: false,
        error: null,
      });
      return;
    }

    if (!pageDataConfig?.mockLoader) {
      return;
    }

    let cancelled = false;

    setMockState({
      data: null,
      isLoading: true,
      error: null,
    });

    pageDataConfig
      .mockLoader()
      .then(mockData => {
        if (cancelled) {
          return;
        }

        setMockState({
          data: mockData,
          isLoading: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        setMockState({
          data: null,
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [configError, data, pageDataConfig, source]);

  if (!page) {
    return renderState("页面未生成", location.pathname);
  }

  const PageComponent = page.Component;
  const isLoading = data === undefined && (source === "remote" ? remoteResult.isLoading : mockState.isLoading);
  const error = data === undefined && source === "remote" ? remoteResult.error : mockState.error;
  const resolvedData = data ?? (source === "remote" ? remoteResult.data : mockState.data);

  if (configError === "mock") {
    return renderState("Mock 数据未配置", pageId);
  }

  if (configError === "remote") {
    return renderState("远端数据未配置", pageId);
  }

  if (isLoading) {
    return renderState("数据加载中", pageId);
  }

  if (error) {
    return renderState("数据加载失败", error.message);
  }

  return <PageComponent data={resolvedData} />;
};

export default GeneratedPageHost;
